import React, { useState, useEffect, useRef } from "react";
import { Form } from "react-bootstrap";
import CustomSelect from "../../../components/form/CustomSelect";
import { useSelector, useDispatch } from "react-redux";
import { fetchActiveCities } from "../../../redux/slice/locationSlice";
import { fetchProfessions } from "../../../redux/slice/allUsersSlice";
import InputField from "../../../components/form/InputField";
import { CheckboxOption, MultiValueMessage, multiMessageStyle } from "../../../components/form/utils";

const companyVerifiedOptions = [
  { value: "verified", label: "Verified Only" },
  { value: "not_verified", label: "Unverified Only" }
];

const HybridRangeFilter = ({ label, prefix, filters, handleChange }) => {
  const [isCustomMode, setIsCustomMode] = useState(false);

  const minKey = `min_${prefix}`;
  const maxKey = `max_${prefix}`;
  const minVal = filters[minKey] || "";
  const maxVal = filters[maxKey] || "";

  let dropdownValue = "any";
  if (minVal === "0" && maxVal === "10") dropdownValue = "0-10";
  else if (minVal === "11" && maxVal === "50") dropdownValue = "11-50";
  else if (minVal === "51" && maxVal === "100") dropdownValue = "51-100";
  else if (minVal === "101" && maxVal === "500") dropdownValue = "101-500";
  else if (minVal === "501" && maxVal === "") dropdownValue = "500+";
  else if (minVal !== "" || maxVal !== "") dropdownValue = "custom";
  else if (isCustomMode) dropdownValue = "custom";

  const options = [
    { value: "any", label: "Any" },
    { value: "0-10", label: "0 to 10" },
    { value: "11-50", label: "11 to 50" },
    { value: "51-100", label: "51 to 100" },
    { value: "101-500", label: "101 to 500" },
    { value: "500+", label: "500+" },
    { value: "custom", label: "Custom Range..." },
  ];

  const handleDropdownChange = (selected) => {
    const val = selected ? selected.value : "any";
    if (val === "custom") {
      setIsCustomMode(true);
      handleChange(minKey, "");
      handleChange(maxKey, "");
    } else {
      setIsCustomMode(false);
      if (val === "any") {
        handleChange(minKey, "");
        handleChange(maxKey, "");
      } else if (val === "0-10") {
        handleChange(minKey, "0");
        handleChange(maxKey, "10");
      } else if (val === "11-50") {
        handleChange(minKey, "11");
        handleChange(maxKey, "50");
      } else if (val === "51-100") {
        handleChange(minKey, "51");
        handleChange(maxKey, "100");
      } else if (val === "101-500") {
        handleChange(minKey, "101");
        handleChange(maxKey, "500");
      } else if (val === "500+") {
        handleChange(minKey, "501");
        handleChange(maxKey, "");
      }
    }
  };

  return (
    <div>
      <CustomSelect
        label={{ name: label }}
        options={options}
        value={dropdownValue}
        onChange={handleDropdownChange}
        className="w-100 mb-2"
        isClearable={false}
      />
      {dropdownValue === "custom" && (
        <div className="grid gap-2">
          <div className="g-col-6">
            <Form.Control
              type="number"
              placeholder="Min"
              value={minVal}
              onChange={(e) => handleChange(minKey, e.target.value)}
            />
          </div>
          <div className="g-col-6">
            <Form.Control
              type="number"
              placeholder="Max"
              value={maxVal}
              onChange={(e) => handleChange(maxKey, e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const AppUserFilterDrawer = ({ initialFilters, onChange, onApply }) => {
  const dispatch = useDispatch();

  const { cities = [] } = useSelector((state) => state.location || {});
  const { professions = [] } = useSelector((state) => state.allUsers || {});
  const { plans = [] } = useSelector((state) => state.subscription || {});

  const cityOptions = cities.map(c => ({ value: c.id, label: c.name }));
  const professionOptions = professions.map(p => ({ value: p.id, label: p.name }));

  const citySearchTimeoutRef = useRef(null);
  const professionSearchTimeoutRef = useRef(null);

  const handleCitySearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (citySearchTimeoutRef.current) clearTimeout(citySearchTimeoutRef.current);
      citySearchTimeoutRef.current = setTimeout(() => {
        dispatch(fetchActiveCities({ search: inputValue, limit: 20 }));
      }, 400);
    } else if (action === "menu-close") {
      dispatch(fetchActiveCities({ limit: 20 }));
    }
  };

  const handleProfessionSearch = (inputValue, { action }) => {
    if (action === "input-change") {
      if (professionSearchTimeoutRef.current) clearTimeout(professionSearchTimeoutRef.current);
      professionSearchTimeoutRef.current = setTimeout(() => {
        dispatch(fetchProfessions({ search: inputValue, limit: 20 }));
      }, 400);
    } else if (action === "menu-close") {
      dispatch(fetchProfessions({ limit: 20 }));
    }
  };

  const [selectedCityObjects, setSelectedCityObjects] = useState(() =>
    (initialFilters?.city_id || []).map(id => cityOptions.find(c => c.value === id) || { value: id, label: "Selected City" })
  );

  const [selectedProfessionObjects, setSelectedProfessionObjects] = useState(() =>
    (initialFilters?.profession_id || []).map(id => professionOptions.find(p => p.value === id) || { value: id, label: "Selected Profession" })
  );

  const planOptions = [
    { value: "free", label: "Free" },
    ...plans.map(p => ({ value: p.id, label: p.name }))
  ];

  const [selectedPlanObjects, setSelectedPlanObjects] = useState(() =>
    (initialFilters?.plan_id || []).map(id => planOptions.find(p => p.value === id) || { value: id, label: id === "free" ? "Free" : "Selected Plan" })
  );

  const mergedCityOptions = [
    ...selectedCityObjects,
    ...cityOptions.filter(c => !selectedCityObjects.some(sc => sc.value === c.value))
  ];

  const mergedProfessionOptions = [
    ...selectedProfessionObjects,
    ...professionOptions.filter(p => !selectedProfessionObjects.some(sp => sp.value === p.value))
  ];

  const mergedPlanOptions = [
    ...selectedPlanObjects,
    ...planOptions.filter(p => !selectedPlanObjects.some(sp => sp.value === p.value))
  ];

  const [filters, setFilters] = useState(initialFilters || {
    city_id: [],
    profession_id: [],
    min_connections: "", max_connections: "",
    min_followers: "", max_followers: "",
    min_companies: "", max_companies: "",
    company_verified_status: null,
    verification_status: null,
    plan_id: [],
    migration_status: null,
    activity: "all",
    active_today: null,
    active_last_7_days: null,
    inactive_days: "",
    never_logged_in: null,
    last_login_start: null,
    last_login_end: null,
    start_date: "", end_date: ""
  });

  // Whenever filters change, call the parent's onChange so it has the latest ref
  useEffect(() => {
    if (onChange) {
      onChange(filters);
    }
  }, [filters, onChange]);

  const handleChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="d-flex flex-column gap-3">
      <CustomSelect
        label={{ name: "City" }}
        options={mergedCityOptions}
        value={selectedCityObjects}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        selectProps={{
          components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
          styles: multiMessageStyle
        }}
        placeholder="Select City"
        onInputChange={handleCitySearch}
        onChange={(selected) => {
          const selectedArr = selected || [];
          setSelectedCityObjects(selectedArr);
          handleChange("city_id", selectedArr.map(s => s.value));
        }}
      />

      <CustomSelect
        label={{ name: "Profession" }}
        options={mergedProfessionOptions}
        value={selectedProfessionObjects}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        selectProps={{
          components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
          styles: multiMessageStyle
        }}
        placeholder="Select Profession"
        onInputChange={handleProfessionSearch}
        onChange={(selected) => {
          const selectedArr = selected || [];
          setSelectedProfessionObjects(selectedArr);
          handleChange("profession_id", selectedArr.map(s => s.value));
        }}
      />

      <CustomSelect
        label={{ name: "Subscription Plan" }}
        options={mergedPlanOptions}
        value={selectedPlanObjects}
        isMulti
        isClearable
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        selectProps={{
          components: { Option: CheckboxOption, MultiValue: MultiValueMessage },
          styles: multiMessageStyle
        }}
        placeholder="Select Plan"
        onChange={(selected) => {
          const selectedArr = selected || [];
          setSelectedPlanObjects(selectedArr);
          handleChange("plan_id", selectedArr.map(s => s.value));
        }}
      />

      <HybridRangeFilter
        label="Connections"
        prefix="connections"
        filters={filters}
        handleChange={handleChange}
      />

      <HybridRangeFilter
        label="Followers"
        prefix="followers"
        filters={filters}
        handleChange={handleChange}
      />

      <HybridRangeFilter
        label="Companies"
        prefix="companies"
        filters={filters}
        handleChange={handleChange}
      />

      <HybridRangeFilter
        label="Posts"
        prefix="posts"
        filters={filters}
        handleChange={handleChange}
      />

      <CustomSelect
        label={{ name: "Company Verified Status" }}
        options={companyVerifiedOptions}
        value={filters.company_verified_status}
        isClearable
        placeholder="Any"
        onChange={(selected) => handleChange("company_verified_status", selected ? selected.value : null)}
      />

      <CustomSelect
        label={{ name: "User Verification Status" }}
        options={[
          { value: 'verified', label: 'Verified' },
          { value: 'pending', label: 'Not Verified' }
        ]}
        value={filters.verification_status}
        isClearable
        placeholder="Any"
        onChange={(selected) => handleChange("verification_status", selected ? selected.value : null)}
      />

      <CustomSelect
        label={{ name: "Activity" }}
        options={[
          { value: "all", label: "All Activity" },
          { value: "active_today", label: "Active Today" },
          { value: "active_last_7_days", label: "Active (Last 7 Days)" },
          { value: "inactive_7", label: "Inactive 7+ Days" },
          { value: "inactive_10", label: "Inactive 10+ Days" },
          { value: "inactive_30", label: "Inactive 30+ Days" },
          { value: "inactive_90", label: "Inactive 90+ Days" },
          { value: "inactive_180", label: "Dormant Users (180+ Days)" },
          { value: "never_logged_in", label: "Never Logged In" },
          { value: "custom", label: "Custom Range" }
        ]}
        value={filters.activity || "all"}
        onChange={(selected) => {
          const val = selected ? selected.value : "all";
          setFilters(prev => ({
            ...prev,
            activity: val,
            active_today: val === 'active_today' ? true : null,
            active_last_7_days: val === 'active_last_7_days' ? true : null,
            inactive_days: val.startsWith('inactive_') ? val.split('_')[1] : "",
            never_logged_in: val === 'never_logged_in' ? true : null,
            last_login_start: val === 'custom' ? prev.last_login_start : null,
            last_login_end: val === 'custom' ? prev.last_login_end : null,
          }));
        }}
      />

      {filters.activity === "custom" && (
        <InputField
          label={{ name: "Last Login Range", class: "text-muted fw-semibold mb-2" }}
          input={{
            type: "date-range",
            value: { start: filters.last_login_start, end: filters.last_login_end },
            placeholder: "Select date range",
            onChange: (e) => {
              const val = e.target.value;
              handleChange("last_login_start", val?.start || "");
              handleChange("last_login_end", val?.end || "");
            }
          }}
        />
      )}

      <InputField
        label={{ name: "Created At" }}
        input={{
          type: "date-range",
          value: { start: filters.start_date, end: filters.end_date },
          placeholder: "Select date range",
          onChange: (e) => {
            const val = e.target.value;
            handleChange("start_date", val?.start || "");
            handleChange("end_date", val?.end || "");
          }
        }}
      />
    </div>
  );
};

export default AppUserFilterDrawer;
